import classNames from 'classnames';
import ReactMarkdown from 'react-markdown';

import type { LlmVerdict } from '../../types';

interface LlmVerdictDisplayProps {
  verdict: LlmVerdict;
}

const CONFIDENCE_LABEL: Record<LlmVerdict['confidence'], string> = {
  high: 'High confidence',
  medium: 'Medium confidence',
  low: 'Low confidence'
};

// Reusable display for the LLM-drafted portion of any judgment-row finding. Shows:
//   - the verdict text in a callout block,
//   - a confidence chip,
//   - the grounded-in citations as a bulleted list so the user can verify each claim.
// Doesn't render any check-specific data — that's the wrapping renderer's job.
export const LlmVerdictDisplay = ({ verdict }: LlmVerdictDisplayProps) => (
  <div className='llm-verdict'>
    <div className='llm-verdict__header'>
      <span className='llm-verdict__label'>Drafted by Claude</span>
      <span
        className={classNames(
          'llm-verdict__confidence',
          `llm-verdict__confidence--${verdict.confidence}`
        )}
      >
        {CONFIDENCE_LABEL[verdict.confidence]}
      </span>
    </div>
    <div className='llm-verdict__text'>
      {/*
          Claude is asked to format verdicts with minimal markdown (bullets + bold) so
          dense audit findings stay scannable. We restrict allowed tags to avoid headings /
          code blocks / images that wouldn't fit the audit aesthetic, and historical plain-
          text verdicts in the DB render fine as plain paragraphs.
        */}
      <ReactMarkdown
        allowedElements={['p', 'strong', 'em', 'ul', 'ol', 'li', 'br']}
        unwrapDisallowed
      >
        {verdict.draft}
      </ReactMarkdown>
    </div>
    {verdict.groundedIn.length > 0 && (
      <div className='llm-verdict__grounded'>
        <div className='llm-verdict__grounded-title'>Grounded in</div>
        <ul className='llm-verdict__grounded-list'>
          {verdict.groundedIn.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      </div>
    )}
  </div>
);
